import React, { useState, useCallback } from 'react';
import { UserPlus, ChevronDown, ChevronUp } from 'lucide-react';
import { Player } from '../Model';
import { LoadingButton } from '../LoadingButton';

type PlayerFormProps = {
  onAddPlayer: (player: Player) => Promise<void>;
  onUpdatePlayer?: (player: Player) => Promise<void>;
  selectedPlayer?: Player | null;
  onClearSelection?: () => void;
  skillNames: string[];
  selectionDataId: number;
};

function PlayerForm({ onAddPlayer, onUpdatePlayer, selectedPlayer, onClearSelection, skillNames, selectionDataId }: PlayerFormProps) {
  const [name, setName] = useState('');
  const [nicknames, setNicknames] = useState('');
  const [isArchived, setIsArchived] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSkills, setShowSkills] = useState(false);

  const createInitialSkills = useCallback((): Record<string, number> => {
    return skillNames.reduce((acc, skill) => {
      acc[skill] = 1;
      return acc;
    }, {} as Record<string, number>);
  }, [skillNames]);

  const [skills, setSkills] = useState(() => createInitialSkills());

  // Update form when selectedPlayer changes
  React.useEffect(() => {
    if (selectedPlayer) {
      setName(selectedPlayer.name);
      setNicknames(selectedPlayer.nicknames || '');
      setIsArchived(selectedPlayer.isArchived || false);
      // Ensure all skill names have values, defaulting to 1 if missing
      const playerSkills = createInitialSkills();
      skillNames.forEach(skill => {
        playerSkills[skill] = selectedPlayer.skills[skill] || 1;
      });
      setSkills(playerSkills);
    } else {
      setName('');
      setNicknames('');
      setIsArchived(false);
      setSkills(createInitialSkills());
    }
  }, [selectedPlayer, createInitialSkills, skillNames]);

  const handleSkillChange = (skill: string, value: string) => {
    setSkills({ ...skills, [skill]: Number(value) });
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      const totalScore = Object.values(skills).reduce((sum, score) => sum + score, 0);
      const trimmedNicknames = nicknames.trim() || undefined;

      if (selectedPlayer) {
        // Update existing player
        await onUpdatePlayer?.({
          ...selectedPlayer,
          name,
          nicknames: trimmedNicknames,
          skills: { ...skills },
          totalScore,
          isArchived,
          selectionDataId: selectedPlayer.selectionDataId,
          version: selectedPlayer.version
        });
      } else {
        // Add new player
        await onAddPlayer({ id: 0, version:0, name, nicknames: trimmedNicknames, skills: { ...skills }, totalScore, isArchived, selectionDataId: selectionDataId });
      }

      // Clear form
      setName('');
      setNicknames('');
      setIsArchived(false);
      setSkills(createInitialSkills());
      onClearSelection?.();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error submitting player:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setName('');
    setNicknames('');
    setIsArchived(false);
    setSkills(createInitialSkills());
    onClearSelection?.();
  };

  const isEditing = !!selectedPlayer;

  // When editing, always show skills
  const skillsVisible = isEditing || showSkills;

  return (
    <div className={`player-form-card ${isEditing ? 'player-form-card-editing' : ''}`}>
      <div className="player-form-header">
        <div className="player-form-title">
          <UserPlus size={16} />
          <span>{isEditing ? 'Edit Player' : 'Quick Add Player'}</span>
        </div>
        {isEditing && (
          <span className="player-form-editing-name">{selectedPlayer.name}</span>
        )}
      </div>

      <div className="player-form-content">
        <div className="player-form-main-row">
          <input
            type="text"
            placeholder="Player name..."
            value={name}
            onChange={e => setName(e.target.value)}
            className="form-control player-form-name-input"
          />
          <LoadingButton
            isLoading={isSubmitting}
            onClick={handleSubmit}
            disabled={!name.trim()}
            className={`btn btn-sm ${!name.trim() ? 'btn-secondary' : isEditing ? 'btn-warning' : 'btn-success'}`}
            loadingText={isEditing ? 'Saving...' : 'Adding...'}
          >
            {isEditing ? 'Save' : 'Add'}
          </LoadingButton>
          {isEditing && (
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              className="btn btn-sm btn-secondary"
            >
              Cancel
            </button>
          )}
        </div>

        {!isEditing && (
          <button
            type="button"
            onClick={() => setShowSkills(!showSkills)}
            className="player-form-toggle"
          >
            {showSkills ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <span>{showSkills ? 'Hide skills' : 'Set skills'}</span>
          </button>
        )}

        {skillsVisible && (
          <div className="player-form-skills">
            {skillNames.map(skill => (
              <label key={skill} className="player-form-skill-row">
                <span className="player-form-skill-name">{skill.charAt(0).toUpperCase() + skill.slice(1)}</span>
                <div className="player-form-skill-controls">
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => handleSkillChange(skill, String(val))}
                      className={`player-form-skill-btn ${skills[skill] === val ? 'player-form-skill-btn-active' : ''}`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </label>
            ))}
            <div className="player-form-nicknames">
              <label className="player-form-nicknames-label">Nicknames</label>
              <textarea
                value={nicknames}
                onChange={e => setNicknames(e.target.value)}
                placeholder="Alternative names (one per line)&#10;e.g., Johnny&#10;Jon"
                className="player-form-nicknames-textarea"
                rows={3}
              />
              <span className="player-form-nicknames-hint">
                Used for matching when pasting from WhatsApp
              </span>
            </div>
            <div className="player-form-archived">
              <label className="player-form-archived-label">
                <input
                  type="checkbox"
                  checked={isArchived}
                  onChange={e => setIsArchived(e.target.checked)}
                  className="player-form-archived-checkbox"
                />
                <span>Archived</span>
              </label>
              <span className="player-form-archived-hint">
                Archived players are hidden by default and excluded from Squad Analysis
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlayerForm;
